import type { MetricUnit, Money } from '@aurora-pvi/contracts';

export type MetricPrimitive = number | string | Money | null;

function isMoney(value: unknown): value is Money {
  return typeof value === 'object' && value !== null && 'amount' in value && 'currency' in value;
}

const compactCurrency = (amount: number, currency: string): string => {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  const symbol = currency === 'USD' ? '$' : `${currency} `;
  if (abs >= 1_000_000_000) return `${sign}${symbol}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${symbol}${abs.toFixed(2)}`;
};

/**
 * Format a metric for display. Returns null for a null value rather than "0" —
 * the contract is explicit that a missing measurement must never read as zero.
 */
export function formatMetricValue(
  value: MetricPrimitive,
  unit: MetricUnit,
  options: { compact?: boolean } = {},
): string | null {
  if (value === null || value === undefined) return null;
  const compact = options.compact ?? true;

  if (isMoney(value)) {
    const amount = Number(value.amount);
    if (Number.isNaN(amount)) return null;
    return compact
      ? compactCurrency(amount, value.currency)
      : new Intl.NumberFormat('en-US', { style: 'currency', currency: value.currency }).format(
          amount,
        );
  }

  if (typeof value === 'string') return value;

  switch (unit) {
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'ratio':
      return `${value.toFixed(2)}x`;
    case 'score':
      return String(Math.round(value));
    case 'currency':
      return compactCurrency(value, 'USD');
    case 'days':
      return `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })} days`;
    case 'hours':
      return `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })} hrs`;
    case 'fte':
      return `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })} FTE`;
    case 'count':
    case 'itemsPerPeriod':
    case 'deploymentsPerPeriod':
      return compact && Math.abs(value) >= 1_000_000
        ? `${(value / 1_000_000).toFixed(1)}M`
        : value.toLocaleString('en-US');
    case 'currencyPerRelease':
      return `${compactCurrency(value, 'USD')} / release`;
    case 'text':
    default:
      return value.toLocaleString('en-US');
  }
}

function toNumber(value: MetricPrimitive): number | null {
  if (value === null || value === undefined) return null;
  if (isMoney(value)) {
    const n = Number(value.amount);
    return Number.isNaN(n) ? null : n;
  }
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export interface Delta {
  absolute: number;
  percent: number | null;
  direction: 'up' | 'down' | 'flat';
}

export function computeDelta(current: MetricPrimitive, comparison: MetricPrimitive): Delta | null {
  const a = toNumber(current);
  const b = toNumber(comparison);
  if (a === null || b === null) return null;
  const absolute = a - b;
  const percent = b === 0 ? null : (absolute / Math.abs(b)) * 100;
  const direction = absolute > 0 ? 'up' : absolute < 0 ? 'down' : 'flat';
  return { absolute, percent, direction };
}

/**
 * Whether a movement is good, bad or merely contextual. Needed because a rise
 * in cost and a rise in revenue must not both render green.
 */
export function deltaSentiment(
  direction: Delta['direction'],
  desired: 'Up' | 'Down' | 'Context',
): 'positive' | 'negative' | 'neutral' {
  if (direction === 'flat' || desired === 'Context') return 'neutral';
  if (desired === 'Up') return direction === 'up' ? 'positive' : 'negative';
  return direction === 'down' ? 'positive' : 'negative';
}

export function formatDelta(delta: Delta, unit: MetricUnit): string {
  if (delta.percent !== null && unit !== 'percent' && unit !== 'score') {
    return `${delta.percent >= 0 ? '+' : ''}${delta.percent.toFixed(1)}%`;
  }
  if (unit === 'percent') {
    return `${delta.absolute >= 0 ? '+' : ''}${(delta.absolute * 100).toFixed(1)} pp`;
  }
  return `${delta.absolute >= 0 ? '+' : ''}${delta.absolute.toLocaleString('en-US', {
    maximumFractionDigits: 1,
  })}`;
}

export function formatTimestamp(iso: string | null): string {
  if (!iso) return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function formatRelativeTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'never refreshed';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'unknown';
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
  const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
  ];
  let value = -seconds;
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) return rtf.format(Math.round(value), unit);
    value /= size;
  }
  return rtf.format(Math.round(value), 'year');
}
